'use client';

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { Toast } from '@/types/ui.types';
import { ToastContainer } from '@/components/ui/feedback/toast-container';

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Toast 알림 시스템 Provider
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    const duration = toast.duration ?? 3000;

    // 최대 3개의 toast만 유지
    setToasts(prev => [...prev.slice(-2), { ...toast, id, duration }]);

    // 자동 제거
    setTimeout(() => {
      removeToast(id);
    }, duration);

    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

/**
 * Toast 알림을 사용하기 위한 hook
 * @throws {Error} ToastProvider 외부에서 사용하는 경우
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }

  return useMemo(
    () => ({
      success: (message: string) => context.addToast({ type: 'success', message }),
      error: (message: string) => context.addToast({ type: 'error', message }),
      warning: (message: string) => context.addToast({ type: 'warning', message }),
      info: (message: string) => context.addToast({ type: 'info', message }),
    }),
    [context.addToast]
  );
}
